import { InitGeneralPublicResponse } from '../../../types/lender/init-types'
import { AdditionalYields } from '../../../types'
import { INIT_MODES } from './publicCallBuild'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import {
  computeUtilization,
  convertRateToApr,
  parseRawAmount,
} from '../../../utils/parsing'
import { GenericTokenList } from '../../../types'
import { getInitUnderlyingToPoolMap } from '../addresses/init'
import { createMarketUid } from '../../../utils'
import { lenderShortName } from '../../../utils/marketName'

const INIT_POOL_NAME_OVERRIDES: Record<string, string> = {
  '0x592c91ac727da556dc90ddf5630e1901efcd0c92': 'Init FBTC Isolated',
}

// Init's liquidation incentive (a `liqIncentiveMultiplier`, 1e18-scaled, where
// penalty = multiplier/1e18 - 1) lives on the mode/pool config, but the
// `InitLens.getMarketData` struct we read here does NOT surface it (it only
// returns collateralFactor / borrowFactor / maxHealthAfterLiq / caps / rates /
// totals). Until a getter is added, default to the common 5%.
// TODO: fetch the real per-mode liqIncentiveMultiplier and set
// `liquidationPenalty = liqIncentiveMultiplier / 1e18 - 1`.
const INIT_DEFAULT_LIQUIDATION_PENALTY = 0.05

// Init Capital is winding down: every pool is frozen. No new deposits or
// borrows are possible, but existing positions can still be repaid and
// withdrawn (the user-state path honours `isFrozen` by zeroing borrowable
// while leaving withdrawable intact). Set deterministically for all pools.
const INIT_POOLS_FROZEN = true

export const INIT_EMODE_LABELS: { [mode: number]: string } = {
  [1]: 'Default',
  [2]: 'Blue Chip Volatile',
  [3]: 'Stablecoins',
  [4]: 'ETH correlated',
  [5]: 'Ethena',
  [6]: 'BTC',
}

export const getInitReservesDataConverter = (
  lender: string,
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
): [(data: any[]) => InitGeneralPublicResponse | undefined, number] => {
  const expectedNumberOfCalls = INIT_MODES.length
  const underlyingToPoolMap = getInitUnderlyingToPoolMap(chainId, lender)
  return [
    (data: any[]) => {
      if (data.length !== expectedNumberOfCalls) {
        return undefined
      }
      const modeOne = 0
      const result: Record<string, any> = {}
      data[modeOne]?.forEach((dat: any) => {
        const asset = dat?.underlying.toLowerCase()
        const meta = tokenList[asset]
        const totalDeposits = parseRawAmount(
          dat?.totalSupply.toString(),
          meta?.decimals,
        )
        const totalDebt = parseRawAmount(
          dat?.totalDebt.toString(),
          meta.decimals,
        )
        const oracleKey =
          toOracleKey(meta?.assetGroup!) ?? toGenericPriceKey(asset, chainId)
        const price = prices[oracleKey] ?? 0
        const liquidity = Number(totalDeposits) - Number(totalDebt)
        const poolId = underlyingToPoolMap.get(asset)
        const params = !!poolId ? { metadata: { poolId } } : undefined
        if (!poolId) return
        const marketUid = createMarketUid(chainId, lender, poolId)
        const name =
          INIT_POOL_NAME_OVERRIDES[poolId.toLowerCase()] ??
          lenderShortName(lender) + ' ' + (meta?.symbol ?? '')
        const borrowCap = Number(parseRawAmount(
          dat?.borrowCap.toString(),
          meta.decimals,
        ))
        const borrowLiq = borrowCap > 0
          ? Math.min(liquidity, Math.max(0, borrowCap - Number(totalDebt)))
          : liquidity
        result[asset] = {
          marketUid,
          name,
          poolId: asset,
          underlying: asset,
          asset: meta,
          borrowCap,
          canBurn: Boolean(dat?.canBurn),
          canFlash: Boolean(dat?.canFlash),
          canMint: Boolean(dat?.canMint),
          canRepay: Boolean(dat?.canRepay),
          totalDeposits,
          totalDebt,
          totalDepositsUSD: price * Number(totalDeposits),
          totalDebtUSD: price * Number(totalDebt),
          totalLiquidity: liquidity,
          borrowLiquidity: borrowLiq,
          totalLiquidityUSD: liquidity * price,
          borrowLiquidityUSD: borrowLiq * price,
          utilization: computeUtilization(totalDeposits, totalDebt),
          depositRate: convertRateToApr(
            Number(parseRawAmount(dat?.supplyRate.toString(), 18)),
          ),
          variableBorrowRate: convertRateToApr(
            Number(parseRawAmount(dat?.borrowRate.toString(), 18)),
          ),
          stableBorrowRate: 0,
          collateralActive: true,
          intrinsicYield:
            additionalYields?.intrinsicYields?.[oracleKey] ?? 0,
          rewards: [],
          config: {},
          closeFactor: 1,
          // Reserve still exists (positions must remain visible & withdrawable);
          // only new deposits/borrows are blocked → active but frozen.
          isActive: true,
          isFrozen: INIT_POOLS_FROZEN,
          borrowingEnabled: INIT_POOLS_FROZEN ? false : totalDebt !== '0',
          depositsEnabled: !INIT_POOLS_FROZEN,
          params,
        }
      })

      let eModes: any = {}
      INIT_MODES.forEach((mode, index) => {
        data[index]?.forEach((multicallData: any) => {
          const asset = multicallData?.underlying.toLowerCase()
          const cf = Number(parseRawAmount(multicallData.collateralFactor, 18))
          const modeData = {
            category: mode,
            label: INIT_EMODE_LABELS[mode],
            borrowCollateralFactor: cf,
            collateralFactor: cf,
            borrowFactor: Number(
              parseRawAmount(multicallData.borrowFactor, 18),
            ),
            maxHealthAfterLiq: Number(
              parseRawAmount(multicallData.maxHealthAfterLiq, 18),
            ),
            debtDisabled: false,
            collateralDisabled: false,
            liquidationPenalty: INIT_DEFAULT_LIQUIDATION_PENALTY,
            closeFactor: 1,
          }
          if (!eModes[mode]) eModes[mode] = modeData
          result[asset].config[mode] = modeData
        })
      })

      Object.entries(eModes).forEach(([modeId, modeData]: [string, any]) => {
        Object.entries(result).forEach(([key, _]: [string, any]) => {
          if (!result[key].config[modeId]) {
            result[key].config[modeId] = {
              label: modeData.label,
              category: modeData.category,
              debtDisabled: true,
              collateralDisabled: true,
              collateralFactor: 0,
              borrowCollateralFactor: 0,
              borrowFactor: 340282366920938500000, // disabled flag
              liquidationPenalty: 0,
              closeFactor: 1,
            }
          }
        })
      })

      return { data: result, chainId }
    },
    expectedNumberOfCalls,
  ]
}
